import { OnePieceCard } from '../types/onepiece';
import { findOnePieceCard } from './onepieceData';
import { PokemonCard, isPokemonChaseCard } from '../types/pokemon';
import { tcgDataService } from '../services/tcgDataService';

export interface CardOwnershipBase {
  cardId: string;
  setId: string;
  owned: boolean;
  quantity: number;
  condition?: string;
  purchasePrice?: number;
  purchaseDate?: string;
  location?: string;
  language?: string;
  notes?: string;
  marketPrice?: number; // Live price from API
  currentPrice?: number;
  inCollection?: boolean;
  gradingCompany?: string;
  gradingGrade?: string;
  gradingId?: string;
  isGrading?: boolean;
  links?: string[];
}

export interface EnrichedCard extends CardOwnershipBase {
  id: string;
  cardName: string;
  justtcgName?: string;
  justtcgId?: string;
  setName: string;
  setSeries?: string;
  number: string;
  condition: string; // Override optional from base
  language: string;
  costBasis: number;
  currentValue: number;
  imageUrl: string;
  rarity?: string;
  ownershipId: string;
  tags: string[];
  game: 'pokemon' | 'onepiece';
}

/**
 * Load One Piece card data for a specific set
 */
export async function loadOnePieceSetData(setId: string): Promise<Record<string, OnePieceCard>> {
  try {
    // Convert setId format: "OP01" -> "op-01", "ST01" -> "st-01"
    const formattedSetId = setId
      .toLowerCase()
      .replace(/^([a-z]+)(\d+)$/, '$1-$2');

    console.log(`[CardEnrichment] Loading One Piece set: ${setId}`);

    const cards: any[] = await tcgDataService.getOnePieceSetCards(formattedSetId);

    const cardsMap: Record<string, OnePieceCard> = {};
    cards.forEach((card: any) => {
      const cardId = card.card_image_id || card.card_set_id;
      if (cardId) {
        cardsMap[cardId] = {
          id: cardId,
          cardId: cardId,
          setId: card.set_id || setId,
          name: card.card_name || 'Unknown',
          nameJp: card.nameJp,
          justtcgName: card.justtcg_name,
          justtcgId: card.justtcg_id,
          number: card.card_set_id || '',
          rarity: card.rarity || 'C',
          cost: card.card_cost ? parseInt(card.card_cost) : undefined,
          attribute: card.attribute,
          power: card.card_power ? parseInt(card.card_power) : undefined,
          counter: card.counter_amount,
          color: card.card_color ? [card.card_color] : [],
          type: card.sub_types ? [card.sub_types] : [],
          effect: card.card_text,
          trigger: card.trigger,
          category: card.card_type,
          images: {
            small: card.card_image ? card.card_image.replace(/\/([^/]+)\.(jpg|png)$/i, '/thumbnail/$1.webp') : '',
            large: card.card_image || '',
            smallJp: card.card_image_jp_thumbnail || '',
            largeJp: card.card_image_jp || '',
          },
          prices: {
            low: card.inventory_price,
            mid: card.inventory_price,
            high: card.market_price,
            market: card.market_price,
          },
          isChaseCard: ['SR', 'SEC', 'L', 'SP'].includes(card.rarity?.trim() || ''),
        };
      }
    });

    console.log(`[CardEnrichment] Loaded ${Object.keys(cardsMap).length} cards from set ${setId}`);
    return cardsMap;
  } catch (error) {
    console.error(`[CardEnrichment] Error loading set ${setId}:`, error);
    return {};
  }
}

/**
 * Enrich card ownership data with metadata from card JSON files
 */
export async function enrichCardData<T extends CardOwnershipBase>(
  ownerships: T[],
  game: 'pokemon' | 'onepiece',
  userId?: string
): Promise<EnrichedCard[]> {
  if (game === 'onepiece') {
    // Load all unique sets
    const uniqueSetIds = Array.from(new Set(ownerships.map((o) => o.setId)));
    const cardsMap: Record<string, OnePieceCard> = {};

    await Promise.all(
      uniqueSetIds.map(async (setId) => {
        const setData = await loadOnePieceSetData(setId);
        Object.assign(cardsMap, setData);
      })
    );

    // Enrich each ownership record
    return ownerships.map((own) => {
      // Safety check for undefined cardId
      if (!own.cardId) {
        console.warn('[CardEnrichment] Skipping card with undefined cardId:', own);
        return {
          ...own,
          id: 'unknown',
          cardName: 'Unknown Card',
          setName: own.setId || 'Unknown',
          number: '???',
          condition: own.condition || 'NEAR_MINT',
          language: 'EN',
          costBasis: own.purchasePrice || 0,
          currentValue: 0,
          imageUrl: '',
          ownershipId: 'unknown',
          tags: [],
          game: 'onepiece' as const,
        };
      }

      const baseCardId = own.cardId.replace(/_p\d+$/, '');
      let card: OnePieceCard | undefined = cardsMap[own.cardId];

      // Fallback to base card ID if variant not found
      if (!card) {
        card = cardsMap[baseCardId];
      }

      
      // Use centralized helper for robust lookup
      if (!card) {
        card = findOnePieceCard(Object.values(cardsMap), own.cardId);
      }

      const cardName = card ? card.name : own.cardId;
      const isJp = own.language?.toUpperCase() === 'JP';
      const imageUrl = card 
        ? (isJp && card.images.smallJp ? card.images.smallJp : card.images.small || card.images.large)
        : '';
      const number = card ? card.number : '???';
      // Use live price from ownership if available (populated by backend), otherwise fallback to JSON data
      const marketPrice = own.marketPrice || own.currentPrice || card?.prices?.market || card?.prices?.mid || 0;

      return {
        ...own,
        id: own.cardId,
        cardName,
        justtcgName: card?.justtcgName,
        justtcgId: card?.justtcgId,
        setName: own.setId,
        number,
        condition: own.condition || 'NEAR_MINT', // Explicitly set as required string
        language: own.language || 'EN',
        costBasis: own.purchasePrice || 0,
        currentValue: marketPrice,
        imageUrl,
        rarity: card?.rarity,
        ownershipId: userId ? `${userId}-${own.cardId}` : own.cardId,
        inCollection: own.inCollection,
        tags: [],
        game: 'onepiece' as const,
      };
    });
  }

  // Load all unique Pokemon sets
  const uniqueSetIds = Array.from(new Set(ownerships.map((o) => o.setId)));
  const cardsMap: Record<string, PokemonCard> = {};

  await Promise.all(
    uniqueSetIds.map(async (setId) => {
      try {
        const cards: any[] = await tcgDataService.getPokemonSetCards(setId);
        
        cards.forEach((card: any) => {
          cardsMap[card.id] = {
            ...card,
            images: {
              small: card.images?.small || card.thumbnailUrl || '',
              large: card.images?.large || card.imageUrl || '',
            },
          };
        });
      } catch (err) {
        console.warn(`[CardEnrichment] Failed to load Pokemon data for set ${setId}`, err);
      }
    })
  );

  // Enrich each ownership record
  return ownerships.map((own) => {
    const card = cardsMap[own.cardId];
    const cardName = card ? card.name : own.cardId;
    const imageUrl = card ? card.images.small : '';
    const setName = card?.set?.name || own.setId;
    const number = card ? card.number : '???';

    // Calculate market price
    let marketPrice = 0;
    if (card?.tcgplayer?.prices) {
      const prices = Object.values(card.tcgplayer.prices);
      if (prices.length > 0) {
        marketPrice = prices[0].market || prices[0].mid || 0;
      }
    }

    return {
      ...own,
      id: own.cardId,
      cardName,
      setName,
      setSeries: card?.set?.series,
      number,
      condition: own.condition || 'NEAR_MINT',
      language: own.language || 'EN',
      costBasis: own.purchasePrice || 0,
      currentValue: marketPrice,
      imageUrl,
      rarity: card?.rarity,
      isChaseCard: card ? isPokemonChaseCard(card) : false,
      ownershipId: userId ? `${userId}-${own.cardId}` : own.cardId,
      inCollection: own.inCollection,
      tags: [],
      game: 'pokemon' as const,
    };
  });
}
